import React from 'react';

import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';

import { Skeleton } from '../../../../atoms/Skeleton';

type Variant = 'list' | 'compact' | 'grid';

export type Props = {
  hasGreyBackground: boolean;
  title: string;
  variant: Variant;
};

const useStyles = makeStyles((theme: Theme) => ({
  buttonContainer: {
    alignItems: 'flex-end',
    display: 'flex',
  },
  buttonSkeleton: {
    height: '36px',
    width: '36px',
  },
  contentContainer: {
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  imageSkeleton: {
    height: '80px',
    marginRight: theme.spacing(1),
    width: '80px',
  },
  list: {
    margin: 0,
    padding: theme.spacing(1, 0, 0),

    '& li': {
      border: `1px solid ${theme.palette.grey[400]}`,
      marginBottom: theme.spacing(2),
      padding: theme.spacing(1),
    },
    '& li:last-child': {
      marginBottom: 0,
    },
  },
  listCompact: {
    margin: 0,
    padding: theme.spacing(1, 0, 0),

    '& li': {
      border: 'none',
      borderBottom: `1px solid ${theme.palette.grey[400]}`,
      marginBottom: 0,
      padding: theme.spacing(1, 0),
    },
    '& li:last-child': {
      borderBottom: 'none',
    },
    '&.grey-background': {
      '& li': {
        padding: theme.spacing(1),
      },
    },
  },
  listGrid: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    margin: 0,
    padding: theme.spacing(1, 0, 0),

    '& li': {
      border: `1px solid ${theme.palette.grey[400]}`,
      flexBasis: `calc(50% - ${theme.spacing(1)})`,
      padding: theme.spacing(1),
    },
  },
  listItem: {
    background: theme.palette.common.white,
    display: 'flex',
    justifyContent: 'space-between',
  },
  textContainer: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  textWidth10: {
    width: '10%',
  },
  textWidth20: {
    width: '20%',
  },
  textWidth30: {
    width: '30%',
  },
  textWidth50: {
    width: '50%',
  },
  textWidth60: {
    width: '60%',
  },
}));

export function MenuItemsListSkeleton(props: Props): JSX.Element {
  const { hasGreyBackground, title = '', variant } = props;
  const classes = useStyles();

  const isListVariant = variant === 'list';
  const isCompactVariant = variant === 'compact';
  const isGridVariant = variant === 'grid';

  const rootStyles = clsx('fd-menu-items-list-skeleton');
  const menuItemsListStyles = clsx(
    'fd-menu-items-list-skeleton__list',
    hasGreyBackground && 'grey-background',
    isListVariant && classes.list,
    isCompactVariant && classes.listCompact,
    isGridVariant && classes.listGrid,
  );

  const renderMenuItemsSkeleton = (): JSX.Element[] =>
    Array(isGridVariant ? 4 : 3)
      .fill(true)
      .map((_, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <li className={classes.listItem} key={`menu-items-list-skeleton-${i}`}>
          <Skeleton className={classes.imageSkeleton} variant="rect" />
          <div className={classes.contentContainer}>
            <div className={classes.textContainer}>
              <div>
                <Skeleton
                  className={
                    isGridVariant ? classes.textWidth50 : classes.textWidth30
                  }
                />
                <Skeleton
                  className={
                    isGridVariant ? classes.textWidth60 : classes.textWidth50
                  }
                />
              </div>
              <Skeleton
                className={
                  isGridVariant ? classes.textWidth20 : classes.textWidth10
                }
              />
            </div>

            <div className={classes.buttonContainer}>
              <Skeleton className={classes.buttonSkeleton} variant="circle" />
            </div>
          </div>
        </li>
      ));

  return (
    <div className={rootStyles} data-fd="menu-items-list-skeleton">
      {title && <Skeleton className={classes.textWidth30} />}

      <ul className={menuItemsListStyles}>{renderMenuItemsSkeleton()}</ul>
    </div>
  );
}
